/**
 * Deep-link helpers: open a new browser tab with hero search prefilled + autorun.
 */

import { detectInput, TAB_COMPANY, TAB_EMAIL, TAB_PERSON, TAB_PHONE } from '../pages/home/homeSearchUtils';

const MODULES = new Set([TAB_PERSON, TAB_EMAIL, TAB_PHONE, TAB_COMPANY]);

/**
 * @param {string[]} locations
 * @returns {string}
 */
function joinLocations(locations) {
  return (Array.isArray(locations) ? locations : [])
    .map((x) => String(x || '').trim())
    .filter(Boolean)
    .slice(0, 4)
    .join(',');
}

/**
 * Compose corporate hero query: domain + name + locations.
 * @param {{ domain?: string, name?: string, companyName?: string, locations?: string[] }} opts
 * @returns {string}
 */
export function composeCorporatePivotQuery(opts) {
  const domain = String(opts?.domain || '').trim();
  const name = String(opts?.name || opts?.companyName || '').trim();
  const locs = (Array.isArray(opts?.locations) ? opts.locations : [])
    .map((x) => String(x || '').trim())
    .filter(Boolean);
  const parts = [];
  if (domain) parts.push(domain);
  if (name && name.toLowerCase() !== domain.toLowerCase()) parts.push(name);
  locs.forEach((loc) => {
    if (!parts.some((p) => p.toLowerCase() === loc.toLowerCase())) parts.push(loc);
  });
  return parts.join(' ').trim();
}

/**
 * @param {{ module?: string, query: string, domain?: string, name?: string, locations?: string[] }} opts
 * @returns {string}
 */
export function buildInvestigationDeepLink(opts) {
  const query = String(opts?.query || '').trim();
  const usp = new URLSearchParams();
  usp.set('autorun', '1');
  usp.set('q', query);
  const mod = String(opts?.module || '').trim().toLowerCase();
  if (MODULES.has(mod)) usp.set('module', mod);
  const domain = String(opts?.domain || '').trim();
  if (domain) usp.set('domain', domain);
  const name = String(opts?.name || '').trim();
  if (name) usp.set('name', name);
  const locs = joinLocations(opts?.locations);
  if (locs) usp.set('locations', locs);
  const path = typeof window !== 'undefined' ? window.location.pathname || '/' : '/';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${path}?${usp.toString()}`;
}

/**
 * Resolve investigation module from structured pivot fields (not free-text heuristics).
 * Backend `kind` / `tab` win; domain / email / legal-entity cues break ties.
 *
 * @param {object} pivot
 * @returns {'person'|'email'|'phone'|'company'|null}
 */
export function resolvePivotModule(pivot) {
  const kind = String(pivot?.kind || pivot?.tab || '').toLowerCase();
  const email = String(pivot?.email || '').trim();
  const domain = String(pivot?.domain || '').trim();
  const companyName = String(pivot?.company_name || '').trim();
  const personName = String(pivot?.person_name || pivot?.handle || '').trim();
  const handle = String(pivot?.handle || '').trim();
  const label = String(pivot?.label || pivot?.query || '').trim();
  const blob = `${companyName} ${personName} ${label}`;
  const chrome = /\b(wikipedia|telephone numbers|dialing|calling|voip|schema\.?org|w3c?)\b/i;

  if (kind === 'email' || email.includes('@')) return TAB_EMAIL;
  if (kind === 'company' || kind === 'corporate') return TAB_COMPANY;
  if (kind === 'phone') return TAB_PHONE;
  if (kind === 'person' || handle) {
    // Structured person only when it does not look like encyclopedia/VoIP chrome.
    if (chrome.test(blob)) return null;
    return TAB_PERSON;
  }
  if (domain && /\.[a-z]{2,24}$/i.test(domain)) return TAB_COMPANY;
  if (/\b(s\.?r\.?l\.?|s\.?a\.?|ltd|llc|gmbh|inc)\b/i.test(blob)) return TAB_COMPANY;
  if (personName) return TAB_PERSON;
  if (chrome.test(blob)) return null;
  return null;
}

/**
 * Open a new browser tab that will autorun the investigation search.
 * @param {{ module?: string, query: string, domain?: string, name?: string, locations?: string[] }} opts
 * @returns {Window|null}
 */
export function openInvestigationInNewTab(opts) {
  if (typeof window === 'undefined') return null;
  const query = String(opts?.query || '').trim();
  if (!query) return null;
  let module = String(opts?.module || '').trim().toLowerCase();
  if (!MODULES.has(module)) {
    // Structured fields first; free-text detectInput only as last resort for legacy callers.
    if (opts?.domain) module = TAB_COMPANY;
    else if (query.includes('@')) module = TAB_EMAIL;
    else if (/\b(s\.?r\.?l\.?|s\.?a\.?|ltd|llc|gmbh|inc)\b/i.test(query)) module = TAB_COMPANY;
    else module = detectInput(query)?.tab || TAB_PERSON;
  }
  const url = buildInvestigationDeepLink({
    module,
    query,
    domain: opts?.domain,
    name: opts?.name,
    locations: opts?.locations,
  });
  return window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Parse autorun params from the current location search string.
 * @param {string} [search]
 * @returns {{ autorun: boolean, query: string, module: string|null, domain: string, name: string, locations: string[] }|null}
 */
export function parseInvestigationDeepLink(search) {
  const fallback =
    typeof window !== 'undefined' ? window.location.search : '';
  const raw = String((search ?? fallback) || '');
  const usp = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  if (usp.get('autorun') !== '1') return null;
  const query = String(usp.get('q') || '').trim();
  if (!query) return null;
  let module = String(usp.get('module') || '').trim().toLowerCase();
  if (!MODULES.has(module)) {
    module = detectInput(query)?.tab || TAB_PERSON;
  }
  const locations = String(usp.get('locations') || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  return {
    autorun: true,
    query,
    module,
    domain: String(usp.get('domain') || '').trim(),
    name: String(usp.get('name') || '').trim(),
    locations,
  };
}

/**
 * @param {object} pivot Backend discovery pivot
 * @returns {{ module: string, query: string, domain?: string, name?: string, locations?: string[], handle?: string }|null}
 */
export function deepLinkOptsFromPivot(pivot) {
  const kind = String(pivot?.kind || pivot?.tab || '').toLowerCase();
  const domain = String(pivot?.domain || '').trim();
  const companyName = String(pivot?.company_name || '').trim();
  const personName = String(pivot?.person_name || pivot?.handle || '').trim();
  const handle = String(pivot?.handle || '').trim().replace(/^@+/, '');
  const locations = Array.isArray(pivot?.locations)
    ? pivot.locations.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  const rawQuery = String(pivot?.query || pivot?.label || pivot?.email || '').trim();
  const module = resolvePivotModule(pivot);
  if (!module || !rawQuery) return null;

  if (module === TAB_EMAIL || kind === 'email' || String(pivot?.email || '').includes('@')) {
    return { module: TAB_EMAIL, query: String(pivot?.email || rawQuery).trim() };
  }
  if (module === TAB_COMPANY) {
    // Prefer clean domain for Hero prefill / launch (not the verbose composed query).
    const clean = domain || companyName || rawQuery;
    return {
      module: TAB_COMPANY,
      query: clean,
      domain: domain || undefined,
      name: companyName || undefined,
      locations: locations.length ? locations : undefined,
    };
  }
  if (module === TAB_PERSON) {
    const q = handle || personName || rawQuery;
    return {
      module: TAB_PERSON,
      query: q,
      name: personName || q,
      handle: handle || undefined,
      locations: locations.length ? locations : undefined,
    };
  }
  return {
    module,
    query: rawQuery,
    domain: domain || undefined,
    name: companyName || personName || undefined,
    locations: locations.length ? locations : undefined,
  };
}

